import { Button } from '@components/ui/button';
import { ClearableSearchInput } from '@components/ui/clearable-search-input';
import { Popover, PopoverAnchor, PopoverContent } from '@components/ui/popover';
import { PlusIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';

type EntityPickerPopoverProps<T extends { id: string }> = {
  addButtonLabel: string;
  className?: string;
  debounceMs?: number;
  emptyMessage: string;
  isDisabled?: boolean;
  isLoading: boolean;
  onAdd: (item: T) => Promise<void> | void;
  onSearchChange: (value: string) => void;
  options: T[];
  placeholder: string;
  renderOption: (item: T) => ReactNode;
  search: string;
};

/**
 * Shared "search + dropdown + add to list" picker used by Engaged Technologies (MEIA Task modal)
 * and Mission References (Campaign Vignette modal). The parent owns the query and the excluded-IDs
 * filter; this component only wires the popover UI, debounce, and option list.
 */
export const EntityPickerPopover = <T extends { id: string }>(props: EntityPickerPopoverProps<T>) => {
  const {
    addButtonLabel,
    className,
    debounceMs = 300,
    emptyMessage,
    isDisabled = false,
    isLoading,
    onAdd,
    onSearchChange,
    options,
    placeholder,
    renderOption,
    search,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [pendingSearch, setPendingSearch] = useState(search);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onSearchChange(pendingSearch.trim());
    }, debounceMs);
    return () => clearTimeout(timeout);
  }, [pendingSearch, debounceMs, onSearchChange]);

  const handleAdd = async (item: T): Promise<void> => {
    await onAdd(item);
    setPendingSearch('');
    setIsOpen(false);
  };

  return (
    <Popover
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <PopoverAnchor asChild>
        <Button
          className={className}
          disabled={isDisabled}
          onClick={() => setIsOpen(true)}
          type="button"
          variant="link"
        >
          <PlusIcon className="size-4" />
          {addButtonLabel}
        </Button>
      </PopoverAnchor>
      <PopoverContent
        align="end"
        className="w-(--radix-popover-trigger-width) min-w-80 max-w-[480px] p-2"
      >
        <ClearableSearchInput
          className="mb-2"
          onChange={(value) => setPendingSearch(value ?? '')}
          placeholder={placeholder}
          value={pendingSearch}
        />
        <div className="max-h-72 overflow-y-auto rounded-sm border border-border">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading...</div>
          ) : options.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">{emptyMessage}</div>
          ) : (
            options.map((option) => (
              <button
                className="flex w-full cursor-pointer items-start gap-3 border-b border-border p-3 text-left transition-colors last:border-b-0 hover:bg-gray-50"
                key={option.id}
                onClick={() => void handleAdd(option)}
                type="button"
              >
                {renderOption(option)}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
